import api, { handleApiResponse, handleApiError } from './api';

class DashboardService {
  // Get user stats (for customers)
  async getUserStats() {
    try {
      const response = await api.get('/dashboard/user');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get agent stats (for agents)
  async getAgentStats() {
    try {
      const response = await api.get('/dashboard/agent');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get admin stats (for admins)
  async getAdminStats() {
    try {
      const response = await api.get('/dashboard/admin');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Calculate percentage change
  calculatePercentageChange(current, previous) {
    if (!previous || previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  // Format large numbers
  formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  // Format percentage
  formatPercentage(value, decimals = 1) {
    return `${Number(value).toFixed(decimals)}%`;
  }

  // Get trend direction
  getTrendDirection(current, previous) {
    if (!previous || previous === 0) return 'neutral';
    if (current > previous) return 'up';
    if (current < previous) return 'down';
    return 'neutral';
  }

  // Format time duration
  formatDuration(minutes) {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours < 24) {
      return remainingMinutes > 0 
        ? `${hours}h ${remainingMinutes}m`
        : `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    return remainingHours > 0
      ? `${days}d ${remainingHours}h`
      : `${days} day${days !== 1 ? 's' : ''}`;
  }

  // Get status color class
  getStatusColor(status) {
    const statusColors = {
      'active': 'text-green-600 bg-green-100',
      'inactive': 'text-red-600 bg-red-100',
      'warning': 'text-yellow-600 bg-yellow-100',
      'error': 'text-red-600 bg-red-100',
      'unknown': 'text-gray-600 bg-gray-100'
    };
    return statusColors[status] || statusColors.unknown;
  }

  // Get priority color class
  getPriorityColor(priority) {
    const priorityColors = {
      'critical': 'text-red-600 bg-red-100 border-red-200',
      'high': 'text-orange-600 bg-orange-100 border-orange-200',
      'medium': 'text-blue-600 bg-blue-100 border-blue-200',
      'low': 'text-gray-600 bg-gray-100 border-gray-200'
    };
    return priorityColors[priority] || priorityColors.medium;
  }

  // Format chart data for frontend charts
  formatChartData(data, xKey = 'date', yKey = 'count') {
    if (!Array.isArray(data)) return [];
    
    return data.map(item => ({
      x: item[xKey],
      y: item[yKey],
      label: item.label || item[xKey]
    }));
  }

  // Calculate SLA compliance percentage
  calculateSLACompliance(onTime, total) {
    if (!total || total === 0) return 0;
    return Math.round((onTime / total) * 100);
  }

  // Get workload status
  getWorkloadStatus(current, capacity) {
    if (!capacity || capacity === 0) return { status: 'unknown', percentage: 0 };
    
    const percentage = Math.round((current / capacity) * 100);
    let status = 'low';
    
    if (percentage >= 90) {
      status = 'critical';
    } else if (percentage >= 70) {
      status = 'high';
    } else if (percentage >= 50) {
      status = 'medium';
    }
    
    return { status, percentage };
  }

  // Format statistics for display
  formatStatsForDisplay(stats) {
    if (!stats) return null;
    
    return {
      overview: {
        totalTickets: this.formatNumber(stats.total_tickets || 0),
        totalUsers: this.formatNumber(stats.total_users || 0),
        activeTickets: this.formatNumber(stats.active_tickets || 0),
        resolvedTickets: this.formatNumber(stats.resolved_tickets || 0)
      },
      slaCompliance: {
        percentage: this.formatPercentage(stats.sla_compliance?.percentage || 0),
        onTime: stats.sla_compliance?.on_time || 0,
        total: stats.sla_compliance?.total || 0
      },
      escalationRate: this.formatPercentage(stats.escalation_rate || 0)
    };
  }
}

export default new DashboardService();